import {
  Download,
  ListAltOutlined,
  MoreVert,
  TextIncreaseOutlined,
} from "@mui/icons-material";
import {
  DatePicker,
  DesktopDatePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import {
  Box,
  Button,
  Card,
  CardHeader,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { GridFlexEnd, RowCell, TabCell } from "../../components/styled";
import Page from "../../core/components/Page";
import { getUsageStatistics } from "../../services/record.service";
import dayjs, { Dayjs } from "dayjs";
import useAuth from "../../hooks/useAuth";

export default function Usage() {
  const dateFormat = "YYYY-MM-DDTHH:mm:ss.SSSSSS[Z]";

  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs());
  const [dateUntil, setDateUntil] = useState<Dayjs | null>(dayjs());
  const [usages, setUsages] = useState<any>([]);
  const { companyInfo } = useAuth();

  const handleChangeSinceDate = (newValue: any) => {
    setDateFrom(newValue);
  };

  const handleChangeUntilDate = (newValue: any) => {
    setDateUntil(newValue);
  };

  const handleGetUsageStats = async (
    since: string,
    until: string,
    departmentId: string
  ) => {
    const response = await getUsageStatistics(since, until, departmentId);
    setUsages(response?.data);
  };

  const handleDownloadUsageStats = async (
    since: string,
    until: string,
    departmentId: string
  ) => {
    const response = await getUsageStatistics(since, until, departmentId);
    // download response json as file
    const blob = new Blob([JSON.stringify(response?.data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `usage_stats_${since}_to_${until}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!companyInfo?.department_id) return;
    handleGetUsageStats(
      dayjs().format(dateFormat),
      dayjs().format(dateFormat),
      companyInfo.department_id
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo?.department_id]);

  function FileRow({ row }: { row: any }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: any) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleReturnFormattedTime = (time: any) => {
      return time < 60
        ? `${time} saniye`
        : `${Math.floor(time / 60)} dakika ${time % 60} saniye`;
    };

    return (
      <TableRow key={row?.id}>
        <RowCell>{row?.data?.job_id}</RowCell>
        <RowCell>
          {(
            row?.data?.event_specific_data?.total_received_bytes /
            (1024 * 1024)
          ).toFixed(2)}{" "}
          MB
        </RowCell>
        <RowCell>
          {handleReturnFormattedTime(
            row?.data?.event_specific_data?.total_received_duration
          )}
        </RowCell>
        <RowCell>
          {handleReturnFormattedTime(
            row?.data?.event_specific_data?.total_speech_duration
          )}
        </RowCell>

        <RowCell>{moment(row?.ts).format("DD/MM/YYYY hh:mm")}</RowCell>
      </TableRow>
    );
  }

  return (
    <Page title="Raporlar | Boardnote">
      <Card sx={{ mx: 3, p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Box>
            <CardHeader title="Raporlar" />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="Başlangıç Tarihi"
                inputFormat="MM/DD/YYYY"
                value={dateFrom}
                onChange={handleChangeSinceDate}
                renderInput={(params) => <TextField {...params} />}
              />

              <DesktopDatePicker
                label="Bitiş Tarihi"
                inputFormat="MM/DD/YYYY"
                value={dateUntil}
                onChange={handleChangeUntilDate}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>

            <Button
              variant="outlined"
              color="primary"
              startIcon={<ListAltOutlined />}
              onClick={() => {
                if (dateFrom && dateUntil) {
                  handleGetUsageStats(
                    dateFrom.format(dateFormat),
                    dateUntil.format(dateFormat),
                    companyInfo?.department_id
                  );
                }
              }}
            >
              Listele
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<Download />}
              onClick={() => {
                if (dateFrom && dateUntil) {
                  handleDownloadUsageStats(
                    dateFrom.format(dateFormat),
                    dateUntil.format(dateFormat),
                    companyInfo?.department_id
                  );
                }
              }}
            >
              Raporu İndir
            </Button>
          </Box>
        </Stack>

        <TableContainer sx={{ height: "50vh" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TabCell>İş No</TabCell>
                <TabCell>Toplam Dosya Boyutu</TabCell>
                <TabCell>Toplam Dosya Süresi</TabCell>
                <TabCell>Toplam Konuşma Süresi</TabCell>
                <TabCell>Oluşturulma Tarihi</TabCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usages?.payload?.events?.map((row: any) => (
                <FileRow key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <GridFlexEnd sx={{ m: 4 }}>
          <Pagination
            count={1}
            page={1}
            //onChange={handlePageChange}
            color="primary"
          />
        </GridFlexEnd>
      </Card>
    </Page>
  );
}
